import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import includes from 'lodash/includes';
import floor from 'lodash/floor';

import { currencyCode } from 'Helpers/Geo/Country';
import { get as apiGet, post, put, del } from 'Api';
import { universalErrorSet } from 'Redux/ducks/universal-error';
import {
  toggleUniversalErrorModal,
  toggleCheckoutModal,
} from 'Redux/ducks/modal';
import store from 'Redux/store';
import { ERROR_MESSAGE } from 'Redux/ducks/error-messages';
import State from 'Helpers/State';
import { lineViewsSelector, upsellEventItems } from 'Redux/reselects/order-calc/line-view';
import { cartTotalMinorSelector } from 'Redux/reselects/order-calc/cart-readers';
import { toMajor } from 'Redux/reselects/order-calc/cart-money';
import { setCartMessage, resetCartMessage, CART_MESSAGE_DEFAULT, ITEM_UNAVAILABLE, E1 } from 'Redux/ducks/cart-message';
import AttributionUtils from 'Helpers/Attribution/attribution-utils';

const FETCH_CART = 'frontend/cart/FETCH_CART';
const EMPTY_CART = 'frontend/cart/EMPTY_CART';
const ADD_CART = 'frontend/cart/ADD_CART';
const REMOVE_CART = 'frontend/cart/REMOVE_CART';
const PRECHECK_PASS = 'frontend/cart/PRECHECK_PASS';
const ADD_ALL = 'frontend/cart/ADD_ALL';
const REMOVE_ALL = 'frontend/cart/REMOVE_ALL';
const CART_UPSELL = 'frontend/cart/CART_UPSELL';
const SNAPSHOT_REMOVED_ITEM = 'frontend/cart/SNAPSHOT_REMOVED_ITEM';
const CLEAR_REMOVED_ITEM = 'frontend/cart/CLEAR_REMOVED_ITEM';
const CLEAR_ALL_REMOVED_ITEMS = 'frontend/cart/CLEAR_ALL_REMOVED_ITEMS';

const getCartItemsSlugs = items => (items || []).map(i => i.slug);

export function fetchCart() {
  return dispatch => apiGet(dispatch, '/basket')
    .then(({ response, data }) => {
      if (response && response.ok && data) {
        if (Array.isArray(data)) {
          dispatch({
            type: FETCH_CART,
            payload: { data, allSlugs: getCartItemsSlugs(data) },
          });
        } else {
          throw new Error(`Non-Array received in fetchCart - Status: ${response.status} - Type: ${typeof data} - ${data}`);
        }
      }
    });
}

// A brand-new anonymous id can't have a basket server-side, so mark the cart as fetched without a request
export function setEmptyFetchedCart() {
  return { type: FETCH_CART, payload: { data: [], allSlugs: [] } };
}

const decorateCart = cartData => cartData.map((item) => {
  const price = get(item, 'price', []);
  let actualPrice = get(price, currencyCode, 0);
  const currentDiscount = get(item, 'current_discount', 0);
  const percent = get(currentDiscount, 'percent', 0);
  const type = get(item, 'type', 'game');
  if (type !== 'bundle' && percent > 0 && percent < 1) {
    actualPrice *= 1 - percent;
  }
  return { ...item, local_price: floor(actualPrice) };
});

export function doAddToCart(
  push, productId, tierIndex, popup, productSlug, quantity, payWhatYouWant,
) {
  return async (dispatch) => {
    const attribution = AttributionUtils.getAttribution();
    const conversionId = crypto.randomUUID();

    const postData = {
      productSlug, productId, tierIndex, quantity, payWhatYouWant,
    };

    if (attribution?.nonUsMarketingConsent) {
      postData.attribution = attribution;
      postData.attribution.conversionId = conversionId;
    }

    const { response, data } = await post(
      dispatch, `${!isEmpty(payWhatYouWant) ? '/basket/pay-what-you-want' : '/basket'}`, postData,
    );

    resetCartMessage();

    const pageId = State.getPageId() || '';

    if (response.ok) {
      let matchingProduct = data[0];

      if (!productId && productSlug && Array.isArray(data)) {
        matchingProduct = data.filter(product => product.slug === productSlug)[0];
      }
      const updatedCart = decorateCart(data);
      const productAdded = data[data.length - 1];

      const payload = {
        productName: productAdded.name,
        data: updatedCart,
        cartBefore: State.getCartItemsString(),
        allSlugs: getCartItemsSlugs(data),
        userId: State.getUserId(),
        pageId,
        itemAddedId: productId || matchingProduct._id,
        addingLoader: false,
        itemsAddedIds: [],
        itemPrice: (get(productAdded.price, currencyCode, 0) / 100) * quantity,
        conversionId,
      };

      dispatch({ type: ADD_CART, payload });

      if (popup) {
        if (
          !includes(pageId, '/pick-and-mix/') &&
          pageId !== '/cart'
        ) {
          store.dispatch(toggleCheckoutModal());
        }

        if (includes(pageId, '/pick-and-mix/')) {
          push('/cart');
        }
      }

      if (!popup && pageId !== '/cart') {
        push('/cart');
      }
      return false;
    }

    const onProductPage = (
      pageId.indexOf('/game/') !== -1 ||
      pageId.indexOf('/bundle/') !== -1 ||
      pageId.indexOf('/dlc/') !== -1
    );

    if (onProductPage) {
      if (data && data.status === 'E9') {
        return false;
      }

      dispatch({ type: ERROR_MESSAGE, payload: data });
    }

    if (pageId === '/cart') {
      setCartMessage({
        astatus: get(data, 'status', CART_MESSAGE_DEFAULT),
        amessage: get(data, 'message', ''),
        adata: get(data, 'data', {}),
      });
      return false;
    }

    universalErrorSet({
      status: get(data, 'status', CART_MESSAGE_DEFAULT),
      error: get(data, 'message', ''),
    });

    // we don't want to display the error modal when a user is adding a product already in the cart

    store.dispatch(toggleUniversalErrorModal());
    return false;
  };
}

// Legacy function that used to do giveaway checks before add to cart api call
export function addToCart(
  push, productId, tierIndex, popup, quantity = 1, payWhatYouWant,
) {
  store.dispatch(doAddToCart(push, productId, tierIndex, popup, null, quantity, payWhatYouWant));
  return { type: PRECHECK_PASS, payload: null };
}

export function removeFromCart(productId, tierIndex) {
  return async (dispatch) => {
    const { response, data } = await put(dispatch, '/basket', { productId, tierIndex });
    if (!response.ok) throw Error(response.statusText);

    const updatedCart = decorateCart(data);

    const payload = {
      data: updatedCart,
      cartBefore: State.getCartItemsString(),
      userId: State.getUserId(),
      pageId: State.getPageId(),
      allSlugs: getCartItemsSlugs(data),
    };

    dispatch({ type: REMOVE_CART, payload });
  };
}

// Removes an item from the cart immediately, but snapshots it first so the UI
// can render an in slot "Item removed" placeholder with an Undo.
export function removeFromCartWithUndo(item, originalIndex) {
  return async (dispatch, getState) => {
    const payload = { key: item._id, kind: 'item', item, originalIndex };
    dispatch({ type: SNAPSHOT_REMOVED_ITEM, payload });

    try {
      return await removeFromCart(item._id, item.tierIndex)(dispatch, getState);
    } catch (err) {
      dispatch({ type: CLEAR_REMOVED_ITEM, payload: { key: item._id } });
      throw err;
    }
  };
}

export function clearAllRemovedItems() {
  return { type: CLEAR_ALL_REMOVED_ITEMS };
}

export function addToCartFromPixAndMix(productId, productSlug) {
  return async (dispatch) => {
    const attribution = AttributionUtils.getAttribution();
    const conversionId = crypto.randomUUID();

    const postData = { productSlug, productId };

    if (attribution?.nonUsMarketingConsent) {
      postData.attribution = attribution;
      postData.attribution.conversionId = conversionId;
    }

    const { response, data } = await post(
      dispatch, '/basket', postData,
    );
    const astatus = get(data, 'status', '');
    const amessage = get(data, 'message', '');
    if (astatus === ITEM_UNAVAILABLE || astatus === E1) {
      dispatch({ type: ERROR_MESSAGE, payload: amessage });
      universalErrorSet({
        status: astatus,
        error: `Error: ${amessage}`,
      });
      return store.dispatch(toggleUniversalErrorModal());
    }

    const updatedCart = decorateCart(data);

    let matchingProduct = data[0];

    if (!productId && productSlug && Array.isArray(data)) {
      matchingProduct = data.filter(product => product.slug === productSlug)[0];
    }

    const payload = {
      data: updatedCart,
      cartBefore: State.getCartItemsString(),
      userId: State.getUserId(),
      pageId: State.getPageId(),
      itemAddedId: productId || matchingProduct._id,
      addingLoader: false,
      allSlugs: getCartItemsSlugs(data),
      isPickAndMix: true,
      itemsAddedIds: [],
      itemPrice: (get(matchingProduct.price, currencyCode, 0) / 100), // treating as regular price
      conversionId,
    };

    if (!response.ok) throw Error(response.statusText);

    dispatch({ type: ADD_CART, payload });

    return null;
  };
}

export function emptyCart() {
  return async (dispatch) => {
    const url = '/basket';

    const { response } = await del(dispatch, url);

    if (response.ok) {
      dispatch({ type: EMPTY_CART });
    }
  };
}

/**
 * Add a list of products to basket in one request
 * @param {Array.String} products product Ids
 */
export function addAll(products, showCheckoutModal) {
  return async (dispatch) => {
    const attribution = AttributionUtils.getAttribution();
    const conversionId = crypto.randomUUID();

    const postData = { products };

    if (attribution?.nonUsMarketingConsent) {
      postData.attribution = attribution;
      postData.attribution.conversionId = conversionId;
    }

    const { response, data } = await post(
      dispatch, '/basket/setmany', postData,
    );

    if (!response.ok) {
      universalErrorSet({
        status: get(data, 'status', CART_MESSAGE_DEFAULT),
        error: get(data, 'message', ''),
      });

      store.dispatch(toggleUniversalErrorModal());
      return false;
    }

    if (showCheckoutModal) store.dispatch(toggleCheckoutModal());

    const updatedCart = decorateCart(data);

    const itemsAddedIds = data
      .filter(i => products.includes(i._id) || products.includes(i.slug))
      .map(i => i._id);

    const pageId = State.getPageId() || '';
    const payload = {
      data: updatedCart,
      cartBefore: State.getCartItemsString(),
      allSlugs: getCartItemsSlugs(data),
      userId: State.getUserId(),
      itemsAddedIds,
      pageId,
      conversionId,
    };

    return dispatch({ type: ADD_ALL, payload });
  };
}

/**
 * Remove a list of products from basket in one request
 * @param {Array.String} products product Ids
 */
export function removeAll(products) {
  return async (dispatch) => {
    const { response, data } = await post(
      dispatch, '/basket/removemany', { products },
    );

    const updatedCart = decorateCart(data);

    if (!response.ok) {
      universalErrorSet({
        status: get(data, 'status', CART_MESSAGE_DEFAULT),
        error: get(data, 'message', ''),
      });

      store.dispatch(toggleUniversalErrorModal());
      return false;
    }

    const pageId = State.getPageId() || '';
    const payload = {
      data: updatedCart,
      cartBefore: State.getCartItemsString(),
      allSlugs: getCartItemsSlugs(data),
      userId: State.getUserId(),
      pageId,
    };

    return dispatch({ type: REMOVE_ALL, payload });
  };
}

export function removePnmFromCartWithUndo(pnm, originalIndex) {
  return async (dispatch, getState) => {
    const payload = { key: pnm.slug, kind: 'pnm', pnm, originalIndex };
    dispatch({ type: SNAPSHOT_REMOVED_ITEM, payload });

    try {
      return await removeAll(pnm.productIds)(dispatch, getState);
    } catch (err) {
      dispatch({ type: CLEAR_REMOVED_ITEM, payload: { key: pnm.slug } });
      throw err;
    }
  };
}

export function undoCartRemoval(snapshot) {
  return async (dispatch) => {
    if (snapshot.kind === 'pnm') {
      await dispatch(addAll(snapshot.pnm.productIds, false));
    } else {
      const { item } = snapshot;
      await dispatch(doAddToCart(
        () => {},
        item._id,
        item.tierIndex,
        false,
        item.slug,
        item.quantity || 1,
        item.pay_what_you_want,
      ));
    }

    dispatch({
      type: CLEAR_REMOVED_ITEM,
      payload: { key: snapshot.key },
    });
  };
}

/**
 * Dispatch cart upsell event
 * @param {Array.String} products product Ids
 */
export function cartUpsellEvent(items, currencyCodePassed) {
  const state = store.getState();
  const cartItemsSend = upsellEventItems(
    items ?? [],
    lineViewsSelector(state),
    currencyCodePassed,
  );
  const payload = {
    cartCurrency: currencyCodePassed,
    cartTotal: toMajor(cartTotalMinorSelector(state)),
    cartItems: cartItemsSend,
  };
  return store.dispatch({ type: CART_UPSELL, payload });
}
